import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { DEPRECATED_GADGETS_MAP } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import { DeprecationTooltip } from '@atlassian/jira-dashboard-common/src/ui/deprecation-tooltip/index.tsx';
import { checkGadgetType } from '@atlassian/jira-dashboard-common/src/utils/check-gadget-type.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';
import type { GadgetDirectoryItemProps } from './types.tsx';

const GADGET_PREVIEW_WIDTH = 75;

export const GadgetDirectoryItem = ({
	gadgetDirectoryItem,
	onAddGadget,
	getIsLoading,
	getIsDisabled,
}: GadgetDirectoryItemProps) => {
	const { formatMessage } = useIntl();
	const isDeprecated = checkGadgetType(DEPRECATED_GADGETS_MAP, gadgetDirectoryItem.id);

	return (
		<GadgetItemWrapper>
			<GadgetItemContent>
				<GadgetPreview>
					<GadgetThumbnail
						src={gadgetDirectoryItem.thumbnailUri}
						role="img"
						aria-label={gadgetDirectoryItem.titleLabel}
					/>
					<Button
						appearance="primary"
						spacing="compact"
						shouldFitContainer
						onClick={onAddGadget}
						isLoading={getIsLoading(gadgetDirectoryItem.id)}
						isDisabled={getIsDisabled(gadgetDirectoryItem.id)}
						testId="dashboard-edit.ui.header-editable.add-gadget-button.add-gadget-panel.gadget-directory-item.dashboard-add-gadget-button"
						aria-label={formatMessage(messages.addButtonlabel, {
							title: gadgetDirectoryItem.titleLabel,
						})}
					>
						{formatMessage(messages.addThisGadget)}
					</Button>
				</GadgetPreview>
				<GadgetDetailWrapper>
					<Box xcss={gadgetTitleWrapperStyles}>
						<Heading
							as="h3"
							size="xsmall"
							testId="dashboard-edit.ui.header-editable.add-gadget-button.add-gadget-panel.gadget-directory-item.heading"
						>
							{gadgetDirectoryItem.titleLabel}
						</Heading>
						{isDeprecated && fg('endeavour_gadget_deprecation_banner_v1') && <DeprecationTooltip />}
					</Box>
					<Text color="color.text" size="UNSAFE_small">
						{formatMessage(messages.by, {
							authorLabel: gadgetDirectoryItem.authorLabel,
						})}
					</Text>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>{gadgetDirectoryItem.description}</p>
					<CategoryLozengeGroup>
						{gadgetDirectoryItem.categories.map((c) => (
							<Box
								key={c}
								paddingInline="space.100"
								paddingBlock="space.0"
								backgroundColor="color.background.accent.gray.subtler"
								xcss={gadgetCategoryLozengeStyles}
							>
								<Text color="color.text.subtle">{c}</Text>
							</Box>
						))}
					</CategoryLozengeGroup>
				</GadgetDetailWrapper>
			</GadgetItemContent>
		</GadgetItemWrapper>
	);
};

const gadgetTitleWrapperStyles = xcss({
	marginBottom: 'space.025',
	display: 'flex',
	flexDirection: 'row',
});

const gadgetCategoryLozengeStyles = xcss({
	borderRadius: '13px',
	marginRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const GadgetThumbnail = styled.div<{ src: string }>({
	backgroundColor: 'transparent',
	borderRadius: '3px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundImage: ({ src }) => `url(${src})`,
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'contain',
	borderWidth: '1px',
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderColor: `${token('color.border')}`,
	width: '100%',
	height: `${GADGET_PREVIEW_WIDTH / 2}px`,
	marginBottom: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const GadgetPreview = styled.div({
	flex: `0 0 ${GADGET_PREVIEW_WIDTH}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const CategoryLozengeGroup = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
});

// eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/ui-styling-standard/no-styled
const GadgetItemWrapper = styled.div({
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	marginTop: token('space.100'),
	marginRight: token('space.100'),
	marginBottom: token('space.100'),
	marginLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const GadgetDetailWrapper = styled.div({
	paddingLeft: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& p': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text'),
		marginTop: token('space.050'),
		marginBottom: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const GadgetItemContent = styled.div({
	textAlign: 'left',
	display: 'flex',
});
